import { inject } from '@angular/core';

import {
  BaseUserContract,
  canReadCampaigns,
  canWriteInCluster,
  hasAllPermissions,
  hasAnyPermission,
  hasOrgPermission,
  hasPermission,
  hasPermissionInCluster,
  hasRole,
  Permission,
  Role,
  UserContract,
  UserStatus,
} from './types';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';
import { AuthenticationApi } from '../../api/authentication.api';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withRequestStatus } from '../features/request-status.feature';
import { UsersApi } from '../../../client/api/users.api';
import {
  UpdateUserContract,
  UpdateUserMapSettingsContract,
} from '../../../client/store/users/types';
import { PlaceAutocompleteOption } from '../../../client/store/loops/types';
import { v4 } from 'uuid';

type AuthState = {
  loggedInUser: UserContract | undefined;
  usersToImpersonate: BaseUserContract[] | undefined;
  userDesiredPath: string | undefined;
  homeAddressAutocompleteSessionToken: string | undefined;
  homeAddressAutocompleteOptions: PlaceAutocompleteOption[] | undefined;
};

const initialState: AuthState = {
  loggedInUser: undefined,
  usersToImpersonate: undefined,
  userDesiredPath: undefined,
  homeAddressAutocompleteSessionToken: undefined,
  homeAddressAutocompleteOptions: undefined,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus([
    'getLoggedInUser',
    'getUsersToImpersonate',
    'updateUser',
    'updateMapSettings',
    'getHomeAddressAutocompleteOptions',
    'updateMapFilterDefaults',
  ]),
  withMethods(
    (
      store,
      authenticationApi = inject(AuthenticationApi),
      usersApi = inject(UsersApi),
    ) => ({
      getLoggedInUser: rxMethod<void>(
        switchMap(() => {
          return authenticationApi.getLoggedInUser().pipe(
            store.handleActionPipe('getLoggedInUser', undefined),
            tap((loggedInUser) => {
              patchState(store, { loggedInUser });
            }),
          );
        }),
      ),
      getUsersToImpersonate: rxMethod<{
        orgId?: string;
        clusterId?: string;
      }>(
        switchMap(({ orgId, clusterId }) => {
          return usersApi.getUsersForSelection(orgId, clusterId).pipe(
            store.handleActionPipe('getUsersToImpersonate', undefined),
            tap((users) => {
              patchState(store, {
                usersToImpersonate: users.filter(
                  (u) =>
                    u.status === UserStatus.ACTIVE &&
                    u.id !== store.loggedInUser()?.id,
                ),
              });
            }),
          );
        }),
      ),
      updateLoggedInUser: rxMethod<{
        user: UpdateUserContract;
        userId: string;
      }>(
        switchMap(({ user, userId }) => {
          return usersApi.updateUser(user, userId).pipe(
            store.handleActionPipe('updateUser', undefined),
            tap((updatedUser: UserContract) => {
              patchState(store, {
                loggedInUser: {
                  ...store.loggedInUser()!,
                  firstName: updatedUser.firstName,
                  lastName: updatedUser.lastName,
                  preferredLanguage: updatedUser.preferredLanguage,
                  homeAddress: updatedUser.homeAddress,
                },
                homeAddressAutocompleteOptions: undefined,
                homeAddressAutocompleteSessionToken: undefined,
              });
            }),
          );
        }),
      ),
      updateMapSettings: rxMethod<{
        settings: UpdateUserMapSettingsContract;
        userId: string;
      }>(
        switchMap(({ settings, userId }) => {
          return usersApi.updateMapSettings(settings, userId).pipe(
            store.handleActionPipe('updateMapSettings', undefined),
            tap(() => {
              patchState(store, {
                loggedInUser: {
                  ...store.loggedInUser()!,
                  ...settings,
                },
              });
            }),
          );
        }),
      ),
      getHomeAddressAutocompleteOptions: rxMethod<string>(
        pipe(
          debounceTime(300),
          switchMap((input) => {
            if (!store.homeAddressAutocompleteSessionToken()) {
              patchState(store, { homeAddressAutocompleteSessionToken: v4() });
            }
            return usersApi
              .getHomeAddressAutocompleteOptions(
                input,
                store.homeAddressAutocompleteSessionToken()!,
              )
              .pipe(
                store.handleActionPipe(
                  'getHomeAddressAutocompleteOptions',
                  undefined,
                ),
                tap((homeAddressAutocompleteOptions) => {
                  patchState(store, { homeAddressAutocompleteOptions });
                }),
              );
          }),
        ),
      ),
      updateMapFilterDefaults: rxMethod<string[]>(
        pipe(
          debounceTime(1000),
          switchMap((filterKeys) => {
            const userId = store.loggedInUser()!.id;
            return usersApi.updateMapFilterDefaults(userId, filterKeys).pipe(
              store.handleActionPipe('updateMapFilterDefaults', undefined),
              tap(() => {
                patchState(store, {
                  loggedInUser: {
                    ...store.loggedInUser()!,
                    mapFilterDefaults: filterKeys,
                  },
                });
              }),
            );
          }),
        ),
      ),
      setUserDesiredPath: (path: string | undefined) => {
        patchState(store, { userDesiredPath: path });
      },
      hasPermission: (permission: Permission): boolean => {
        const user = store.loggedInUser();
        return user ? hasPermission(user, permission) : false;
      },
      hasPermissionInCluster: (
        permission: Permission,
        clusterId: string,
      ): boolean => {
        const user = store.loggedInUser();
        return user
          ? hasPermissionInCluster(user, permission, clusterId)
          : false;
      },
      hasOrgPermission: (permission: Permission): boolean => {
        return hasOrgPermission(store.loggedInUser(), permission);
      },
      hasAnyPermission: (permissions: Permission[]): boolean => {
        const user = store.loggedInUser();
        return user ? hasAnyPermission(user, permissions) : false;
      },
      hasAllPermissions: (permissions: Permission[]): boolean => {
        const user = store.loggedInUser();
        return user ? hasAllPermissions(user, permissions) : false;
      },
      canWriteInCluster: (clusterId: string): boolean => {
        const user = store.loggedInUser();
        return user ? canWriteInCluster(user, clusterId) : false;
      },
      canReadCampaigns: (clusterId: string | undefined): boolean => {
        const user = store.loggedInUser();
        return user ? canReadCampaigns(user, clusterId) : false;
      },
      hasRole: (role: Role, only: boolean = false): boolean => {
        const user = store.loggedInUser();
        return user ? hasRole(user, role, only) : false;
      },
    }),
  ),
);
